import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./FindTargetObject.css";
import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";
import {
  calculateProgress,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateSelectionAccuracy,
  calculateErrorCount,
} from "../shared/gameMetrics";

const SYMBOLS = [
  "🐶",
  "🐱",
  "🐭",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🐸",
  "🐵",
  "🐧",
  "🦁",
  "🐯",
  "🍎",
  "🍌",
  "🍇",
  "🍓",
  "🍊",
  "🍐",
  "🚗",
  "🚲",
  "🚀",
  "✈️",
  "⚽",
  "🏀",
  "🎈",
  "🎁",
  "⭐",
  "🌸",
  "🦋",
  "📚",
  "🔑",
  "🧸",
  "☂️",
  "🧩",
  "🎵",
  "📷",
];

const SIMILAR_GROUPS = [
  ["🐶", "🐺", "🦊"],
  ["🐱", "🐯", "🦁"],
  ["🍎", "🍅", "🍊"],
  ["⭐", "🌟", "✨"],
  ["🚗", "🚕", "🚙"],
  ["⚽", "🏐", "🏀"],
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 6,
    targetCount: 2,
    columns: 3,
    similarDistractors: false,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 12,
    targetCount: 3,
    columns: 4,
    similarDistractors: false,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 20,
    targetCount: 4,
    columns: 5,
    similarDistractors: true,
  },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]];
  }

  return copy;
}

function createRound(config) {
  let target;
  let distractorPool;

  if (config.similarDistractors) {
    const group = randomItem(SIMILAR_GROUPS);
    target = randomItem(group);

    distractorPool = [
      ...group.filter((symbol) => symbol !== target),
      ...SYMBOLS.filter((symbol) => !group.includes(symbol)),
    ];
  } else {
    target = randomItem(SYMBOLS);
    distractorPool = SYMBOLS.filter((symbol) => symbol !== target);
  }

  const targetItems = Array.from(
    { length: config.targetCount },
    (_, index) => ({
      id: `target-${index}`,
      symbol: target,
      isTarget: true,
    }),
  );

  const distractorItems = Array.from(
    { length: config.itemCount - config.targetCount },
    (_, index) => ({
      id: `distractor-${index}`,
      symbol: randomItem(distractorPool),
      isTarget: false,
    }),
  );

  return {
    target,
    targetCount: config.targetCount,
    items: shuffle([...targetItems, ...distractorItems]),
  };
}

export default function FindTargetObject() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctRounds, setCorrectRounds] = useState(0);
  const [correctSelections, setCorrectSelections] = useState(0);
  const [wrongSelections, setWrongSelections] = useState(0);
  const [omissions, setOmissions] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds],
  );

  const totalErrors = calculateErrorCount(wrongSelections, omissions);
  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

const averageReactionTime =
  calculateAverageReactionTime(responses);

const totalTime = calculateTotalTime(
  startTime,
  endTime
);
  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficultyOrConfig = selectedDifficulty || "easy") => {
    const { difficultyKey, config: nextConfig } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG,
    );

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));
    setSelectedIds([]);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleItem = (itemId) => {
    if (!started || finished) return;

    setSelectedIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId],
    );
  };

  const confirmRound = () => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;

    const selectedItems = round.items.filter((item) =>
      selectedIds.includes(item.id),
    );

    const roundCorrectSelections = selectedItems.filter(
      (item) => item.isTarget,
    ).length;

    const roundWrongSelections = selectedItems.filter(
      (item) => !item.isTarget,
    ).length;

    const roundOmissions = round.targetCount - roundCorrectSelections;

    const isPerfectRound = roundWrongSelections === 0 && roundOmissions === 0;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        target: round.target,
        selectedSymbols: selectedItems.map((item) => item.symbol),
        correctSelections: roundCorrectSelections,
        wrongSelections: roundWrongSelections,
        omissions: roundOmissions,
        isCorrect: isPerfectRound,
        reactionTime,
      },
    ]);

    if (isPerfectRound) {
      setCorrectRounds((previous) => previous + 1);
    }

    setCorrectSelections((previous) => previous + roundCorrectSelections);

    setWrongSelections((previous) => previous + roundWrongSelections);

    setOmissions((previous) => previous + roundOmissions);

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setSelectedIds([]);
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setRound(null);
    setSelectedIds([]);
    setRoundIndex(0);
    setStarted(false);
    setFinished(false);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Busca los objetos iguales"
        description="Observa el objeto modelo y selecciona todos los dibujos iguales que aparecen abajo."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Atención visual",
          "Discriminación visual",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Cantidad de dibujos",
            min: 4,
            max: 40,
            defaultValue: 12,
          }),

          GameFields.targetCount({
            label: "Cantidad de objetos iguales",
            min: 1,
            max: 10,
            defaultValue: 3,
            validate: (value, values) => {
              if (Number(value) >= Number(values.itemCount)) {
                return "La cantidad de objetos iguales debe ser menor que la cantidad total de dibujos.";
              }

              return "";
            },
          }),

          GameFields.columns({
            max: 6,
            defaultValue: 4,
          }),

          GameFields.similarDistractors({
            label: "Usar dibujos parecidos",
            help: "Hace más difícil distinguir el objeto correcto.",
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  return (
    <div className="find-object-page">
      <div className="find-object-card">
        <header className="find-object-header">
          <div>
            <h1>Busca los objetos iguales</h1>

            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="target-object-box">
              <span>Objeto modelo</span>
              <strong>{round.target}</strong>
            </section>

            <section className="find-object-instruction">
              <h2>Selecciona todos los objetos iguales</h2>

              <p>Puede aparecer más de una vez.</p>
            </section>

            <section
              className="find-object-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`find-object-item ${
                    selectedIds.includes(item.id) ? "selected" : ""
                  }`}
                  onClick={() => toggleItem(item.id)}
                  aria-pressed={selectedIds.includes(item.id)}
                >
                  {item.symbol}
                </button>
              ))}
            </section>

            <button
              type="button"
              className="confirm-object-button"
              onClick={confirmRound}
              disabled={selectedIds.length === 0}
            >
              Confirmar selección
            </button>

            <section className="find-object-score">
              <span>Rondas correctas: {correctRounds}</span>

              <span>Selecciones correctas: {correctSelections}</span>

              <span>Errores: {wrongSelections}</span>

              <span>Omisiones: {omissions}</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Rondas correctas",
                  value: correctRounds,
                },
                {
                  label: "Selecciones correctas",
                  value: correctSelections,
                },
                {
                  label: "Errores",
                  value: wrongSelections,
                },
                {
                  label: "Omisiones",
                  value: omissions,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}
